import { X, Save, Search, BookOpen, GraduationCap, Maximize2, Minimize2, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import api from "../api";

const GradeManagement = ({ onClose, role }) => {
  const [subjects, setSubjects] = useState([]);
  const [selectedDiplo, setSelectedDiplo] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [step, setStep] = useState(1); // 1: Diplo, 2: Subject, 3: Grades

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get("subjects/");
        setSubjects(res.data);
      } catch (err) {
        console.error("Error cargando materias", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const diplomaturas = useMemo(() => {
    const diplos = {};
    subjects.forEach(s => {
      if (!diplos[s.diploma]) {
        diplos[s.diploma] = { 
          id: s.diploma, 
          name: s.diploma_name,
          municipio: s.diploma_municipio
        };
      }
    });
    return Object.values(diplos);
  }, [subjects]);

  const filteredSubjects = useMemo(() => {
    return subjects.filter(s => s.diploma === selectedDiplo);
  }, [subjects, selectedDiplo]);

  const handleDiploSelect = (diploId) => {
    setSelectedDiplo(diploId);
    setStep(2);
  };

  const handleSubjectSelect = async (subject) => {
    setLoading(true);
    setSelectedSubject(subject);
    try {
      const res = await api.get(`subjects/${subject.id}/students/`);
      setStudents(res.data);
      setStep(3);
    } catch (err) {
      console.error("Error cargando alumnos", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = (studentId, value) => {
    setStudents((prev) =>
      prev.map((s) => (s.id === studentId ? { ...s, grade: value } : s)),
    );
  };

  const saveGrades = async () => {
    setLoading(true);
    try {
      await api.post(`subjects/${selectedSubject.id}/bulk-grades/`, {
        grades: students.map(s => ({ id: s.id, grade: s.grade })),
      });
      alert("Notas guardadas correctamente");
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Error al guardar las notas";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`modal show d-block animate-fade-in ${isFullscreen ? 'p-0' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className={`modal-dialog modal-dialog-centered transition-all ${isFullscreen ? 'm-0 mw-100 h-100 modal-fullscreen' : 'modal-lg'}`} style={isFullscreen ? { width: '100%', maxWidth: '100%' } : {}}>
        <div className={`modal-content glassmorphism border-0 shadow-lg transition-all ${isFullscreen ? 'rounded-0 h-100 overflow-auto w-100' : 'rounded-4'}`} style={isFullscreen ? { minHeight: '100vh', width: '100vw' } : {}}>
          <div className="modal-header border-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fs-4 fw-bold text-dark d-flex align-items-center">
              <GraduationCap size={28} className="me-2 text-primary" /> Gestión de Notas
            </h5>
            <div className="d-flex gap-2 ms-auto">
              <button 
                type="button" 
                onClick={() => setIsFullscreen(!isFullscreen)} 
                className="btn-glass p-2 d-flex align-items-center justify-content-center"
                style={{ width: '36px', height: '36px', borderRadius: '10px' }}
                title={isFullscreen ? "Contraer" : "Ampliar"}
              >
                {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
              <button 
                type="button" 
                className="btn-close-premium" 
                onClick={onClose}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="modal-body p-4">
            {/* Breadcrumbs / Stepper */}
            <nav aria-label="breadcrumb" className="mb-4">
              <ol className="breadcrumb mb-0 bg-light p-2 rounded-3 px-3">
                <li className={`breadcrumb-item small fw-bold ${step === 1 ? 'text-primary' : 'text-muted'}`}>
                  {step > 1 ? (
                    <button onClick={() => setStep(1)} className="btn btn-link btn-sm p-0 m-0 text-decoration-none text-muted">Diplomaturas</button>
                  ) : 'Diplomaturas'}
                </li>
                {step >= 2 && (
                  <li className={`breadcrumb-item small fw-bold ${step === 2 ? 'text-primary' : 'text-muted'}`}>
                    {step > 2 ? (
                      <button onClick={() => setStep(2)} className="btn btn-link btn-sm p-0 m-0 text-decoration-none text-muted">Materias</button>
                    ) : 'Materias'}
                  </li>
                )}
                {step === 3 && (
                  <li className="breadcrumb-item active small fw-bold text-primary" aria-current="page">Calificaciones</li>
                )}
              </ol>
            </nav>

            {loading && step !== 3 && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-2 text-muted small">Cargando información...</p>
              </div>
            )}

            {!loading && step === 1 && (
              <div className="animate-fade-in-up">
                <h6 className="mb-3 fw-bold text-muted small text-uppercase letter-spacing">1. Seleccione una Diplomatura</h6>
                <div className="row g-3">
                  {diplomaturas.map(diplo => (
                    <div key={diplo.id} className="col-12">
                      <button 
                        onClick={() => handleDiploSelect(diplo.id)}
                        className="btn btn-outline-primary card-diplo-select w-100 text-start p-3 d-flex align-items-center justify-content-between border-2 rounded-3"
                      >
                        <div>
                          <div className="fw-bold">{diplo.name}</div>
                          <div className="small opacity-75">{diplo.municipio} • {subjects.filter(s => s.diploma === diplo.id).length} materias</div>
                        </div>
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  ))}
                  {diplomaturas.length === 0 && (
                    <div className="text-center p-5 bg-light rounded-4">
                      <BookOpen size={48} className="text-muted mb-3 opacity-25" />
                      <p className="text-muted">No tienes materias asignadas para calificar.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!loading && step === 2 && (
              <div className="animate-fade-in-up">
                <div className="d-flex align-items-center mb-3">
                  <button onClick={() => setStep(1)} className="btn btn-link p-0 me-2 text-primary">
                    <ChevronLeft size={24} />
                  </button>
                  <h6 className="mb-0 fw-bold text-muted small text-uppercase letter-spacing">2. Seleccione una Materia</h6>
                </div>
                <div className="row g-3">
                  {filteredSubjects.map(s => (
                    <div key={s.id} className="col-12">
                      <button 
                        onClick={() => handleSubjectSelect(s)}
                        className="btn btn-white shadow-sm w-100 text-start p-3 border-start border-4 border-info h-100 d-flex flex-column justify-content-center rounded-3 hover-lift"
                      >
                        <div className="d-flex align-items-center mb-1">
                          <BookOpen size={16} className="me-2 text-info" />
                          <span className="fw-bold small">{s.name}</span>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="animate-fade-in-up">
                <div className="d-flex align-items-center justify-content-between mb-4 bg-primary bg-opacity-10 p-3 rounded-3 border border-primary border-opacity-25">
                  <div className="d-flex align-items-center">
                    <button onClick={() => setStep(2)} className="btn btn-link p-0 me-3 text-primary">
                      <ChevronLeft size={24} />
                    </button>
                    <div>
                      <h6 className="mb-0 fw-bold text-dark">{selectedSubject?.name}</h6>
                      <p className="mb-0 extreme-small text-muted text-uppercase fw-bold letter-spacing">
                        {diplomaturas.find(d => d.id === selectedDiplo)?.name} ({diplomaturas.find(d => d.id === selectedDiplo)?.municipio})
                      </p>
                    </div>
                  </div>
                  <div className="badge bg-primary rounded-pill px-3 py-2">
                    <UserPlus size={14} className="me-1" /> {students.length} Alumnos
                  </div>
                </div>

                <div className="table-responsive bg-white rounded-3 shadow-sm border overflow-hidden">
                  <table className="table table-hover premium-table align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4 border-0 py-3 small fw-bold text-muted">ALUMNO</th>
                        <th style={{ width: "160px" }} className="text-center border-0 py-3 small fw-bold text-muted">CALIFICACIÓN</th>
                      </tr>
                    </thead>
                    <tbody className="border-top-0">
                      {students.map(student => (
                        <tr key={student.id}>
                          <td className="ps-4 py-3">
                            <div className="d-flex align-items-center">
                              <div className="avatar-sm me-3 bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                                <span className="text-primary fw-bold">{student.full_name?.charAt(0) || 'A'}</span>
                              </div>
                              <div>
                                <div className="fw-bold text-dark">{student.full_name}</div>
                                <div className="text-muted extreme-small">{student.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="pe-4 py-3 text-center">
                            <div className="d-inline-block position-relative">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                step="0.5"
                                className="form-control form-control-lg text-center fw-bold text-primary grade-input"
                                value={student.grade || ""}
                                onChange={(e) => handleGradeChange(student.id, e.target.value)}
                                style={{ width: '90px', border: '2px solid #eef0f2', backgroundColor: '#f8f9fa', borderRadius: '12px' }}
                                placeholder="-"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {students.length === 0 && (
                        <tr>
                          <td colSpan="2" className="text-center p-5 text-muted">
                            <UserPlus size={48} className="text-muted mb-3 opacity-25" />
                            <p>No hay alumnos inscriptos en esta materia.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer border-0 pt-0 p-4">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                {step === 3 && <><span className="dot bg-success"></span> Edición activa</>}
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-light px-4 py-2 fw-bold text-muted" onClick={onClose}>
                  Cerrar
                </button>
                {step === 3 && (
                  <button 
                    className="btn btn-primary d-flex align-items-center px-4 py-2 fw-bold shadow-sm rounded-3" 
                    onClick={saveGrades}
                    disabled={loading || students.length === 0}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    ) : (
                      <Save size={18} className="me-2" />
                    )}
                    {loading ? "Guardando..." : "Guardar Calificaciones"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .letter-spacing { letter-spacing: 0.1em; }
        .extreme-small { font-size: 0.7rem; }
        .hover-lift { transition: all 0.2s ease; }
        .hover-lift:hover { transform: translateY(-3px); }
        .card-diplo-select { transition: all 0.2s ease; }
        .card-diplo-select:hover { background-color: rgba(13, 110, 253, 0.08) !important; color: #0d6efd !important; }
        .card-diplo-select:hover .text-muted { color: #0d6efd !important; opacity: 0.8; }
        .grade-input:focus { border-color: #0d6efd !important; background-color: #fff !important; box-shadow: none !important; }
        .dot { height: 8px; width: 8px; border-radius: 50%; display: inline-block; margin-right: 5px; }
        .transition-all { transition: all 0.3s ease-in-out; }
      `}</style>
    </div>
  );
};

export default GradeManagement;
